import type { CardType } from '../../../samba-payment.types';
import type { JsonValue } from '../../../common.types';
import type { LosslessNumber } from '../../../lossless-json-codec';
import { CardPointeGatewayId } from './cardpointe-system-constants';

export const CardPointeTransactionDetailsOutcome = {
    FOUND: 'found',
    MISSING: 'missing',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CardPointeTransactionDetailsOutcome =
    (typeof CardPointeTransactionDetailsOutcome)[keyof typeof CardPointeTransactionDetailsOutcome];

export const CardPointeGatewayTransactionDetailsType =
    'cardpointe_gateway_transaction_details' as const;

export interface CardPointeTransactionDetailsRequestPathParams {
    readonly retref: string;
    readonly merchid: string;
}

export interface CardPointeTransactionDetailsRequestDetails {
    readonly method: 'GET';
    readonly pathTemplate: '/cardconnect/rest/inquire/{retref}/{merchid}';
    readonly pathParams: CardPointeTransactionDetailsRequestPathParams;
}

export interface CardPointeInquireResponseBody {
    readonly [key: string]: JsonValue | undefined;
    readonly amount?: string;
    readonly orderId?: string;
    readonly resptext?: string;
    readonly setlstat?: string;
    readonly respcode?: string;
    readonly merchid?: string;
    readonly token?: string;
    readonly respproc?: string;
    readonly authdate?: string;
    readonly lastfour?: string;
    readonly name?: string;
    readonly expiry?: string;
    readonly currency?: string;
    readonly retref?: string;
    readonly respstat?: 'A' | 'B' | 'C';
    readonly account?: string;
    readonly bintype?: string;
    readonly entrymode?: string;
    readonly authcode?: string;
    readonly batchid?: string;
    readonly emvTagData?: JsonValue;
    readonly receipt?: JsonValue;
    readonly receiptObj?: JsonValue;
    readonly userfields?: JsonValue;
    readonly voidable?: 'Y' | 'N';
    readonly refundable?: 'Y' | 'N';
    readonly declineCategory?: string;
    readonly declineCategoryText?: string;
    readonly merchAdviceCode?: string;
    readonly merchAdviceText?: string;
    readonly fee_amount?: string;
    readonly fee_value?: string;
    readonly fee_type?: 'SURCHRG' | 'SURCHRG_WAIVED';
    readonly fee_format?: 'percent' | '';
    readonly fee_authcode?: string;
    readonly fee_merchid?: string;
    readonly fee_retref?: string;
}

export interface CardPointeNormalizedGatewayTransactionDetails {
    readonly gateway_id: typeof CardPointeGatewayId;
    readonly gateway_transaction_details_type: typeof CardPointeGatewayTransactionDetailsType;
    readonly gateway_transaction_details_version: 1;
    readonly gateway_txn_id: string;
    readonly base_amount: LosslessNumber;
    readonly total_amount: LosslessNumber;
    readonly total_fee_amount: LosslessNumber;
    readonly total_tax_amount: LosslessNumber;
    readonly is_void: boolean;
    readonly is_settled: boolean;
    readonly refund_amount: LosslessNumber;
    readonly refund_count: number;
    readonly card_brand?: string;
    readonly card_last4?: string;
    readonly cardholder_name?: string;
    readonly card_type?: CardType;
    readonly settlement_status?: string;
    readonly authorization_code?: string;
    readonly batch_id?: string;
}

export type CardPointeTransactionDetailsResponseDetails =
    | {
          readonly outcome: typeof CardPointeTransactionDetailsOutcome.FOUND;
          readonly httpStatus: 200;
          readonly rawDetails: CardPointeInquireResponseBody;
          readonly normalizedDetails: CardPointeNormalizedGatewayTransactionDetails;
      }
    | {
          readonly outcome: typeof CardPointeTransactionDetailsOutcome.MISSING;
          readonly httpStatus: number;
          readonly rawDetails: unknown;
          readonly message: string;
      }
    | {
          readonly outcome: typeof CardPointeTransactionDetailsOutcome.MALFORMED_RESPONSE;
          readonly httpStatus: number;
          readonly rawDetails: unknown;
          readonly message: string;
      };
